import React, { useMemo } from "react";

import { PMIEquipment } from "../../forms/custom-pmi/pmiEquipmentForm";
import { getResultActionOptions } from "../../../utils/data";
import { APP_DATE_FORMAT } from "../../../utils/constants";
import { useAppSelector } from "../../../store/configureStore";
import { getReportDetails } from "../../../store/reportDetails";
import { PMIResultData } from "./createCustomPMI";

import CustomCoverPage from "../custom-general/custom-cover-page";
import CustomInfoSheet from "../custom-general/custom-info-sheet";
import Conditional from "../../common/conditional";
import CustomGeneralPhotos from "../custom-general/custom-general-photos";
import CustomSection from "../custom-general/custom-section";
import ExecutiveSummary from "../custom-visual-inspection/executive-summary";
import IsometricDrawing from "../custom-utm/isometric-drawing";
import PDFTablePage from "../../themed/pdf/pdfTablePage";
import PMIResultTable from "./pmiResultTable";
import OverviewSheet from "../custom-general/overview-sheet";

import withPDF from "../../hoc/withPDF";
import useCoverSheetOverview from "../../../hooks/useCoverSheetOverview";

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface Props {
    reportTitle: string;
    coverData: models.CustomFVIData;
	pmiResultData: PMIResultData;
	pmiEquipmentItems: PMIEquipment[];
    executiveSummary: string;
    projectDetails: models.Project;
    drawing: models.PDFImageProps[];
    photographicData: models.PDFImageProps[];
}

const CustomPMIReport: React.FC<Props> = ({ executiveSummary, drawing, projectDetails, pmiResultData, pmiEquipmentItems, photographicData, coverData, reportTitle }) => {
    const { inspectionTypeName } = projectDetails;
	const { itemCountPerPage } = useAppSelector(getReportDetails);

    const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure.',
			value: coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. Arridex Procedure.',
			value: coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [coverData.coverData]);

	const equipmentColumns: models.Column[] = useMemo(() => {
		return [
			{
				path: 'equipmentName',
				label: 'Equipment Name',
			},
			{
				path: 'modal',
				label: 'Modal',
			},
			{
				path: 'serialNumber',
				label: 'Serial Number',
			},
			{
				path: 'calibrationDueDate',
				label: 'Calibration Due Date',
				content: (equipment: PMIEquipment) => utils.formatTime(equipment.calibrationDueDate, APP_DATE_FORMAT),
			},
			{
				path: 'calibrationBlockDetails',
				label: 'Calibration Block Details',
			},
		];
	}, []);

	const resultSummaryColumns: models.Column[] = useMemo(() => {
		return [
			{
				path: 'id',
				label: 'Sr. No.',
			},
			{
				path: 'itemDescription',
				label: 'Item Description',
			},
			{
				path: 'identifiedMaterialSpec',
				label: 'Identified Material Spec.',
			},
			{
				path: 'result',
				label: 'Result',
				style: (item) => {
					const option = getResultActionOptions().find((option) => utils.matchPhrases(option.id, item.result));
					
					if (option) {
						return {
							backgroundColor: option.color,
							color: '#FFFFFF',
						}
					}

					return {
						backgroundColor: '#000000',
						color: '#FFFFFF',
					}
				}
			},
		];
	}, []);

	const resultsWithSerialNumber = useMemo(() => {
		return pmiResultData.data.map((result, index) => ({
			...result,
			id: index + 1,
		}));
	}, [pmiResultData]);

	const overview = useCoverSheetOverview();
	const inspectionTypeLabel = utils.getExtractedInspectionName(inspectionTypeName);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={inspectionTypeLabel} />

			<CustomInfoSheet
				projectDetails={projectDetails}
				workOrderNumber={coverData.workOrderNumber}
				reportTitle={reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={projectDetails}
			/>

			<ExecutiveSummary summary={executiveSummary} />

			{utils.getChunkedData(resultsWithSerialNumber, itemCountPerPage).map((chunk) => (
				<CustomSection sectionTitle="Result Summary">
					<div className="p-1">
						<PDFTablePage data={chunk} columns={resultSummaryColumns} />
					</div>
				</CustomSection>
			))}
			
			{utils.getChunkedData(pmiEquipmentItems, itemCountPerPage).map((chunk) => (
				<CustomSection sectionTitle="Equipment Details">
					<div className="p-1">
						<PDFTablePage data={chunk} columns={equipmentColumns} />
					</div>
				</CustomSection>
			))}

			{utils.getChunkedData(resultsWithSerialNumber, itemCountPerPage).map((chunk) => (
				<PMIResultTable elements={pmiResultData.elements} pmiResultItems={resultsWithSerialNumber} />
			))}

            <IsometricDrawing label="General Drawing" isoImages={drawing} />

			<Conditional isVisible={photographicData.length > 0}>
				<CustomGeneralPhotos
					photos={photographicData}
					label="Photographic Record"
				/>
			</Conditional>
        </React.Fragment>
	);
};
 
export default withPDF(CustomPMIReport);